import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { tap, map, switchMap, filter, take, catchError } from 'rxjs/operators';
import { Store } from '@ngxs/store';
import { environment } from 'src/environments/environment';
import { PaginatedWoResponse } from '../components/model/paginated-wo.model';
import { WorkOrderFiltersModel } from '../components/model/work-order-filters.model';
import { PaginatedWOLResponse } from '../components/work-order-line-list/model/paginated-wol.model';
import { FinishedProductInformation } from '../components/work-order-line-details/model/finished-product-information.model';
import { WorkOrder } from '../components/model/workorder';
import {
  UpdateCurrentPaginatedWorkOrdersAction,
  UpdateCurrentPaginatedWorkOrderLinesAction,
  UpdateWorkOrderFiltersAction,
  UpdateWorkOrderHeaderMetadataAction
} from '../../../shared/state/WorkOrderState';
import { WorkOrderModel } from '../components/model/work-order.model';
import { WorkOrderState } from '../../../shared/state/WorkOrderState';
import { WorkOrderLineRequestModel } from '../components/work-order-line-details/model/work-order-line-request.model';
import { WorkOrderReportDTO } from '../components/model/work-order-report.model';
import {
  BackOfficeCasingAuditTrailEntry,
  CasingAuditTrailResponse,
  ShopFloorAuditTrailEntry
} from '../components/work-order-line-details/tabs/casing-history-tab/casing-history.model';

export interface Customer {
  customerPk: string;
  customerName: string;
  code: string;
  customerType: string;
  city: string;
  stateProvince: string;
  turnAroundTime: number;
}

interface PaginatedCustomer {
  customerSearchDTOs: Customer[];
  pagination: { totalRecords: number };
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
interface PaginatedCustomerResponse {
  data: PaginatedCustomer;
  dataList: PaginatedCustomer[];
  error: Array<string>;
}

@Injectable({
  providedIn: 'root'
})
export class WorkOrderService {
  public http = inject(HttpClient);
  public store = inject(Store);

  private normalizeWorkOrder(workOrder: WorkOrder & { salesman_fk?: string }): WorkOrder {
    const { salesman_fk, ...rest } = workOrder;
    return rest.salesmanFk !== undefined || salesman_fk === undefined ? rest : { ...rest, salesmanFk: salesman_fk };
  }

  private normalizePaginatedWorkOrderResponse(response: PaginatedWoResponse): PaginatedWoResponse {
    if (!response?.data?.workorders) {
      return response;
    }

    return {
      ...response,
      data: {
        ...response.data,
        workorders: response.data.workorders.map((workOrder) => this.normalizeWorkOrder(workOrder as WorkOrder & { salesman_fk?: string }))
      }
    };
  }

  searchWorkOrders(
    dealerId: string,
    filters: WorkOrderFiltersModel,
    page: number,
    pageSize: number,
    sortBy: string
  ): Observable<PaginatedWoResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.searchWorkOrders.replace('{dealerId}', dealerId)}`;

    const filteredFilters = Object.entries(filters).reduce((acc, [key, value]) => {
      if (value != null && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {} as WorkOrderFiltersModel);

    let params: Record<string, any> = { ...filteredFilters, page: page, pageSize: pageSize };

    const [sortField, sortDir] = sortBy ? sortBy.split(':') : [undefined, undefined];

    if (sortField !== undefined) params = { ...params, sortField: sortField };
    if (sortDir !== undefined) params = { ...params, sortDir: sortDir };

    return this.http
      .get<PaginatedWoResponse>(url, {
        params: params
      })
      .pipe(
        map((response: PaginatedWoResponse) => this.normalizePaginatedWorkOrderResponse(response)),
        tap((response: PaginatedWoResponse) => {
          // Update state after successful API call
          if (response && response.data && response.data.workorders) {
            const items = response.data.workorders || [];
            const count = response.data.pagination.totalRecords || 0;
            this.store.dispatch(new UpdateCurrentPaginatedWorkOrdersAction(items, count));
            this.store.dispatch(new UpdateWorkOrderFiltersAction(filters));
          }
        }),
        switchMap((response: PaginatedWoResponse) => {
          if (response && response.data && response.data.workorders) {
            const expectedCount = response.data.workorders.length;
            // Wait until the store has the expected number of items
            return this.store.select(WorkOrderState.currentPaginatedWorkOrders).pipe(
              filter((workOrders) => workOrders && workOrders.length === expectedCount),
              take(1),
              map(() => response)
            );
          }
          return of(response);
        })
      );
  }

  exportWorkOrders(
    dealerId: string,
    filters: WorkOrderFiltersModel,
    page: number,
    pageSize: number,
    sortBy: string
  ): Observable<PaginatedWoResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.exportWorkOrders.replace('{dealerId}', dealerId)}`;

    const filteredFilters = Object.entries(filters).reduce((acc, [key, value]) => {
      if (value != null && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {} as WorkOrderFiltersModel);

    let params: Record<string, any> = { ...filteredFilters, page: page, pageSize: pageSize };

    const [sortField, sortDir] = sortBy ? sortBy.split(':') : [undefined, undefined];

    if (sortField !== undefined) params = { ...params, sortField: sortField };
    if (sortDir !== undefined) params = { ...params, sortDir: sortDir };

    return this.http
      .get<PaginatedWoResponse>(url, {
        params: params
      })
      .pipe(
        map((response: PaginatedWoResponse) => this.normalizePaginatedWorkOrderResponse(response)),
        tap((response: PaginatedWoResponse) => {
          // Update state after successful API call
          if (response && response.data && response.data.workorders) {
            const items = response.data.workorders || [];
            const count = response.data.pagination.totalRecords || 0;
            this.store.dispatch(new UpdateCurrentPaginatedWorkOrdersAction(items, count));
            this.store.dispatch(new UpdateWorkOrderFiltersAction(filters));
          }
        }),
        switchMap((response: PaginatedWoResponse) => {
          if (response && response.data && response.data.workorders) {
            const expectedCount = response.data.workorders.length;
            // Wait until the store has the expected number of items
            return this.store.select(WorkOrderState.currentPaginatedWorkOrders).pipe(
              filter((workOrders) => workOrders && workOrders.length === expectedCount),
              take(1),
              map(() => response)
            );
          }
          return of(response);
        })
      );
  }

  refreshWorkOrders(
    dealerId: string,
    filters: WorkOrderFiltersModel,
    page: number,
    pageSize: number,
    sortBy: string
  ): Observable<PaginatedWoResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.searchWorkOrders.replace('{dealerId}', dealerId)}`;

    const filteredFilters = Object.entries(filters).reduce((acc, [key, value]) => {
      if (value != null && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {} as WorkOrderFiltersModel);

    let params: Record<string, any> = { ...filteredFilters, page: page, pageSize: pageSize };

    const [sortField, sortDir] = sortBy ? sortBy.split(':') : [undefined, undefined];

    if (sortField !== undefined) params = { ...params, sortField: sortField };
    if (sortDir !== undefined) params = { ...params, sortDir: sortDir };

    return this.http
      .get<PaginatedWoResponse>(url, {
        params: params
      })
      .pipe(
        map((response: PaginatedWoResponse) => this.normalizePaginatedWorkOrderResponse(response)),
        tap((response: PaginatedWoResponse) => {
          // Update state after successful API call
          if (response && response.data && response.data.workorders) {
            const items = response.data.workorders || [];
            const count = response.data.pagination.totalRecords || 0;
            this.store.dispatch(new UpdateCurrentPaginatedWorkOrdersAction(items, count));
          }
        }),
        switchMap((response: PaginatedWoResponse) => {
          if (response && response.data && response.data.workorders) {
            const expectedCount = response.data.workorders.length;
            // Wait until the store has the expected number of items
            return this.store.select(WorkOrderState.currentPaginatedWorkOrders).pipe(
              filter((workOrders) => workOrders && workOrders.length === expectedCount),
              take(1),
              map(() => response)
            );
          }
          return of(response);
        })
      );
  }

  searchWorkOrderLines(
    dealerId: string,
    workOrderId: string,
    page: number,
    pageSize: number,
    workOrderLineId?: string,
    filterValue?: string
  ): Observable<PaginatedWOLResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.searchWorkOrderLine
      .replace('{dealerId}', dealerId)
      .replace('{workOrderHeaderId}', workOrderId)}`;
    let params: any = { page, pageSize };

    // Add workOrderLineId parameter if provided
    if (workOrderLineId) {
      params.workOrderLineId = workOrderLineId;
    }

    // Add filterValue parameter if provided
    if (filterValue) {
      params.filterValue = filterValue;
    }

    return this.http.get<PaginatedWOLResponse>(url, { params }).pipe(
      tap((response: PaginatedWOLResponse) => {
        // Update state after successful API call
        if (response && response.data) {
          const items = response.data.workOrderLines || [];
          this.store.dispatch(new UpdateCurrentPaginatedWorkOrderLinesAction(items));

          // Update header metadata if available
          if (response.data.headerMetadata) {
            this.store.dispatch(
              new UpdateWorkOrderHeaderMetadataAction(workOrderId, {
                dueDayValue: response.data.headerMetadata.dueDayValue,
                showDueDayField: response.data.headerMetadata.showDueDayField,
                custCodeReqValue: response.data.headerMetadata.custCodeReqValue,
                custCodeLabelId: response.data.headerMetadata.custCodeLabelId,
                showMfgDotField: response.data.headerMetadata.showMfgDotField,
                showRtDotField: response.data.headerMetadata.showRtDotField,
                fiscalIdNum: response.data.headerMetadata.fiscalIdNum,
                isWorkOrderEditable: response.data.headerMetadata.isWorkOrderEditable,
                rtDotRequired: response.data.headerMetadata.rtDotRequired
              })
            );
          }
        }
      }),
      switchMap((response: PaginatedWOLResponse) => {
        if (response && response.data && response.data.workOrderLines) {
          const expectedCount = response.data.workOrderLines.length;
          // Wait until the store has the expected number of items
          return this.store.select(WorkOrderState.currentPaginatedWorkOrderLines).pipe(
            filter((workOrderLines) => workOrderLines && workOrderLines.length === expectedCount),
            take(1),
            map(() => response)
          );
        }
        return of(response);
      })
    );
  }

  saveWorkOrderLineChanges(
    dealerId: string,
    workOrderId: string,
    workOrderLineId: string,
    workOrderLineRequestModel: WorkOrderLineRequestModel
  ): Observable<any> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.saveWorkOrderLineChanges
      .replace('{dealerId}', dealerId)
      .replace('{workOrderHeaderId}', workOrderId)
      .replace('{workOrderLineId}', workOrderLineId)}`;

    const payload: WorkOrderLineRequestModel = { ...workOrderLineRequestModel };

    return this.http.post<any>(url, payload);
  }

  fetchWorkOrderLineLocationInfo(dealerId: string, workOrderLineId: string, workOrderHeaderId: string): Observable<any> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.fetchWorkOrderLineLocationInfo
      .replace('{dealerId}', dealerId)
      .replace('{workOrderHeaderId}', workOrderHeaderId)
      .replace('{workOrderLineId}', workOrderLineId)}`;
    return this.http.get<any>(url);
  }

  getFinishedProductInformation(dealerId: string, workOrderId: string, workOrderLineId: string): Observable<FinishedProductInformation> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getFinishedProduct
      .replace('{dealerId}', dealerId)
      .replace('{workOrderHeaderId}', workOrderId)
      .replace('{workOrderLineId}', workOrderLineId)}`;

    return this.http.get<FinishedProductInformation>(url);
  }
  fetchWolShopFloorAuditTrailInfo(
    dealerId: string,
    workOrderId: string,
    workOrderLineId: string,
    localTimeZone: string
  ): Observable<CasingAuditTrailResponse<ShopFloorAuditTrailEntry>> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.fetchWolSfAuditTrailInfo
      .replace('{dealerId}', dealerId)
      .replace('{workOrderHeaderId}', workOrderId)
      .replace('{workOrderLineId}', workOrderLineId)}`;
    return this.http.get<CasingAuditTrailResponse<ShopFloorAuditTrailEntry>>(url, { headers: { localTimeZone } });
  }

  fetchBoAuditTrailByCasingId(
    casingId: string,
    localTimeZone: string
  ): Observable<CasingAuditTrailResponse<BackOfficeCasingAuditTrailEntry>> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.fetchBoAuditTrailByCasing.replace(
      '{casingId}',
      encodeURIComponent(casingId)
    )}`;
    return this.http.get<CasingAuditTrailResponse<BackOfficeCasingAuditTrailEntry>>(url, { headers: { localTimeZone } });
  }

  updateWorkOrderHeader(dealerId: string, workOrderId: string, updateWorkOrderHeaderDTO: WorkOrderModel): Observable<any> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.updateWorkOrderHeader
      .replace('{dealerId}', dealerId)
      .replace('{workOrderHeaderId}', workOrderId)}`;
    return this.http.post<any>(url, updateWorkOrderHeaderDTO);
  }

  searchCustomers(
    dealerId: string,
    userLogin: string,
    plantId: string,
    searchString: string = '',
    pageSize: number = 100
  ): Observable<Customer[]> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.searchUserCustomer}`;

    const headers = new HttpHeaders({
      dealerId: dealerId || '',
      userLogin: userLogin || '',
      plantId: plantId || ''
    });

    const params = new HttpParams()
      .set('searchString', searchString)
      .set('page', '1')
      .set('pageSize', pageSize.toString())
      .set('sortField', 'customerName')
      .set('sortDirection', 'asc');

    return this.http.get<any>(url, { headers, params }).pipe(map((response) => response?.data?.customerSearchDTOs ?? []));
  }

  /**
   * Get customers for dropdown selection based on plant
   * Returns all customers without pagination for dropdown fields
   */
  getCustomersForDropdown(dealerId: string, plantId: string): Observable<any[]> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getCustomersForDropdown}`;

    const headers = new HttpHeaders({
      dealer: dealerId || '',
      plantId: plantId || ''
    });

    return this.http.get<any>(url, { headers }).pipe(
      map((response) => {
        // Response format: { success: true, data: [...], message: null }
        return response?.data ?? [];
      })
    );
  }

  fetchWorkOrderLineInitialLoad(dealerId: string, workOrderId: string, workOrderLineId: string): Observable<any> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getWorkOrderLineEditInitialLoad
      .replace('{dealerId}', dealerId)
      .replace('{workOrderHeaderId}', workOrderId)
      .replace('{workOrderLineId}', workOrderLineId)}`;
    return this.http.get<any>(url);
  }

  refreshSeriesDropdown(dealerId: string, make: string): Observable<any> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.refreshSeries
      .replace('{dealerId}', dealerId)
      .replace('{makeId}', make)}`;
    return this.http.get<any>(url);
  }

  refreshSizesDropdown(dealerId: string, make: string, series: string): Observable<any> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.refreshSizes
      .replace('{dealerId}', dealerId)
      .replace('{makeId}', make)
      .replace('{seriesId}', series)}`;
    return this.http.get<any>(url);
  }

  refreshTreadDropdown(
    dealerId: string,
    workOrderLineId: string,
    make: string,
    series: string | null | undefined,
    size: string,
    moldType: string
  ): Observable<any> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.refreshTread
      .replace('{dealerId}', dealerId)
      .replace('{workOrderLineId}', workOrderLineId)}`;

    let params = new HttpParams();

    if (make != null) params = params.set('makeId', make);
    if (series != null) params = params.set('seriesId', series);
    if (size != null) params = params.set('sizeId', size);
    if (moldType != null) params = params.set('moldType', moldType);

    return this.http.get<any>(url, { params });
  }

  calculateCasingGrade(
    dealerId: string,
    workOrderId: string,
    workOrderLineId: string,
    dto: { processCode: string; mfgDOT: string }
  ): Observable<any> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.calculateCasingGrade
      .replace('{dealerId}', dealerId)
      .replace('{workOrderHeaderId}', workOrderId)
      .replace('{workOrderLineId}', workOrderLineId)}`;
    return this.http.post<any>(url, dto);
  }

  getCasingValue(
    dealerId: string,
    processCode: string,
    customerUuid: string,
    casingSizeUuid: string
  ): Observable<{ casingValue: number | null }> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getCasingValue.replace('{dealerId}', dealerId)}`;

    return this.http
      .get<{ data: { casingValue: number | null } }>(url, {
        params: { processCode, customerUuid, casingSizeUuid }
      })
      .pipe(
        map((response) => ({ casingValue: response?.data?.casingValue ?? null })),
        catchError(() => of({ casingValue: null }))
      );
  }

  markDeleteAndSaveCasing(dealerId: string, workOrderId: string, wokrOrderLineId: string, userName: string): Observable<any> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.markDeleteAndSaveCasing
      .replace('{dealerId}', dealerId)
      .replace('{workOrderHeaderId}', workOrderId)
      .replace('{workOrderLineId}', wokrOrderLineId)}`;

    const params = new HttpParams().set('userName', userName);
    return this.http.post<any>(url, {}, { params });
  }

  markDeleteAndSaveCasingsBulk(dealerId: string, workOrderLineList: string[], userName: string): Observable<any> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.markDeleteAndSaveCasingsBulk.replace('{dealerId}', dealerId)}`;
    const params = new HttpParams().set('userName', userName);
    const payload: any = { selectedWorkOrderLines: workOrderLineList };
    return this.http.post<any>(url, payload, { params });
  }

  markDeleteAndSaveWorkOrder(dealerId: string, workOrderId: string, userName: string): Observable<any> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.markDeleteAndSaveWorkOrder
      .replace('{dealerId}', dealerId)
      .replace('{workOrderHeaderId}', workOrderId)}`;

    const params = new HttpParams().set('userName', userName);
    return this.http.post<any>(url, {}, { params });
  }

  markWorkOrderReleased(dealerId: string, workOrderId: string, userName: string): Observable<any> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.markWorkOrderReleased
      .replace('{dealerId}', dealerId)
      .replace('{workOrderHeaderId}', workOrderId)}`;

    const params = new HttpParams().set('userName', userName);

    return this.http.post<any>(url, {}, { params });
  }

  markSelectedWOLReleased(dealerId: string, workOrderId: string, workOrderLineList: string[], userName: string): Observable<any> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.markSelectedWOLReleased
      .replace('{dealerId}', dealerId)
      .replace('{workOrderHeaderId}', workOrderId)}`;

    const params = new HttpParams().set('userName', userName);
    const payload: any = { selectedWorkOrderLines: workOrderLineList };

    return this.http.post<any>(url, payload, { params });
  }

  /**
   * Fetch full work order details (header + lines) for PDF/report generation.
   * Calls GET /details/{workOrderHeaderPk} with dealerId path variable.
   */
  getWorkOrderDetails(dealerId: string, workOrderHeaderPk: string): Observable<WorkOrderReportDTO> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.workOrderDetails
      .replace('{dealerId}', dealerId)
      .replace('{workOrderHeaderPk}', workOrderHeaderPk)}`;
    return this.http.get<any>(url).pipe(map((response) => response?.data ?? response));
  }
}
